import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getConfig, updateConfig } from "../../services/gfmService";

/**
 * GFM (Guardian Faculty Member) — institute-level configuration page.
 *
 * Backing endpoints (services/gfmService.js):
 *   GET  /gfm/config   → getConfig()
 *   PATCH /gfm/config  → updateConfig(patch)
 * Both are gated by `gfm.manage` (route guard added in App.js).
 *
 * Each of the four config fields is nullable: `null` means "use system default"
 * (the effective value falls through to the institute/system hard default in the
 * backend resolver). The UI exposes that null affordance explicitly:
 *   - booleans (gfmEnabled / gfmAutoReportEnabled): a three-way Select
 *     (Use system default / Enabled / Disabled)
 *   - cadence: a Select including a "Use system default" option
 *   - threshold: a numeric field with a "Use system default" toggle
 *
 * Follows the no-crash frontend rules: every async call is wrapped in try/catch,
 * failures surface as a toast + inline Alert (never a white screen), and all
 * external response access is optional-chained with safe defaults.
 *
 * _Requirements: 5.1, 5.2, 4.9, 10.1_
 */

const CADENCE_OPTIONS = [
  { value: "THREE_DAY", label: "Every 3 days (Mon–Wed / Thu–Sat windows)" },
  { value: "WEEKLY", label: "Weekly (last 7 days)" },
  { value: "MONTHLY", label: "Monthly (previous calendar month)" },
];

// Sentinel used by the boolean Selects to represent the `null` ("use system
// default") choice — Select can't hold `null` cleanly as a value, so we map
// to/from this string at the edges.
const DEFAULT_SENTINEL = "__default__";

const cadenceLabel = (value) => {
  const match = CADENCE_OPTIONS.find((o) => o.value === value);
  return match ? match.label : value ?? "—";
};

const GfmSettings = () => {
  // ─── Institute context (Super Admin dropdown-select flow) ───
  // A Super Admin selecting an institute from the dropdown keeps their SA token
  // (no operatingInstituteId), so we forward `?instituteId=<selectedInstitute.id>`
  // and the backend resolver (resolveActingInstituteContext) picks it up.
  // Null-safe: falls back through auth user → selected institute → null.
  const authUser = useSelector((s) => s?.auth?.user);
  const selectedInstitute = useSelector(
    (s) => s?.instituteContext?.selectedInstitute
  );
  const instituteId = authUser?.instituteId ?? selectedInstitute?.id ?? null;

  // ─── Stored (institute-level) config — each field nullable ───
  const [gfmEnabled, setGfmEnabled] = useState(null);
  const [gfmAutoReportEnabled, setGfmAutoReportEnabled] = useState(null);
  const [gfmNotificationCadence, setGfmNotificationCadence] = useState(null);
  const [gfmDefaulterThreshold, setGfmDefaulterThreshold] = useState(null);
  const [gfmWhatsappEnabled, setGfmWhatsappEnabled] = useState(null);

  // ─── Effective (resolved) values for display context ───
  const [effective, setEffective] = useState(null);

  // ─── UI state ───
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [thresholdError, setThresholdError] = useState(null);

  // ─── Fetch config ───
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const instituteParams = instituteId ? { instituteId } : {};
      const res = await getConfig(instituteParams);
      const data = res?.data?.data ?? res?.data ?? {};
      // Backend returns { stored: {...nullable overrides}, effective: {...} }.
      // Read the nullable per-institute overrides from `stored` (fall back to
      // the flat object for resilience).
      const stored = data.stored ?? data;
      setGfmEnabled(stored.gfmEnabled ?? null);
      setGfmAutoReportEnabled(stored.gfmAutoReportEnabled ?? null);
      setGfmNotificationCadence(stored.gfmNotificationCadence ?? null);
      setGfmDefaulterThreshold(stored.gfmDefaulterThreshold ?? null);
      setGfmWhatsappEnabled(stored.gfmWhatsappEnabled ?? null);
      setEffective(data.effective ?? null);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to load GFM configuration.";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [instituteId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // ─── Boolean tri-state helpers (null | true | false) ───
  const boolToSelect = (v) => {
    if (v == null) return DEFAULT_SENTINEL;
    return v ? "true" : "false";
  };
  const selectToBool = (v) => (v === DEFAULT_SENTINEL ? null : v === "true");

  // ─── Threshold helpers ───
  const thresholdUsesDefault = gfmDefaulterThreshold == null;

  const handleThresholdDefaultToggle = (e) => {
    setThresholdError(null);
    if (e.target.checked) {
      setGfmDefaulterThreshold(null); // use system default
    } else {
      setGfmDefaulterThreshold(75); // a sensible starting explicit value
    }
  };

  const handleThresholdChange = (e) => {
    setThresholdError(null);
    const raw = e.target.value;
    if (raw === "") {
      setGfmDefaulterThreshold("");
      return;
    }
    const n = Number(raw);
    setGfmDefaulterThreshold(Number.isNaN(n) ? raw : n);
  };

  // ─── Validate before save ───
  const validate = () => {
    if (!thresholdUsesDefault) {
      const n = Number(gfmDefaulterThreshold);
      if (
        gfmDefaulterThreshold === "" ||
        !Number.isInteger(n) ||
        n < 0 ||
        n > 100
      ) {
        setThresholdError(
          "Threshold must be a whole number between 0 and 100, or use the system default."
        );
        return false;
      }
    }
    return true;
  };

  // ─── Save (PATCH) ───
  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const patch = {
        gfmEnabled: gfmEnabled,
        gfmAutoReportEnabled: gfmAutoReportEnabled,
        gfmNotificationCadence: gfmNotificationCadence,
        gfmDefaulterThreshold: thresholdUsesDefault
          ? null
          : Number(gfmDefaulterThreshold),
        gfmWhatsappEnabled: gfmWhatsappEnabled,
      };

      const instituteParams = instituteId ? { instituteId } : {};
      const res = await updateConfig(patch, instituteParams);
      const data = res?.data?.data ?? res?.data ?? {};
      const stored = data.stored ?? data;

      // Re-sync from the authoritative server response.
      setGfmEnabled(stored.gfmEnabled ?? null);
      setGfmAutoReportEnabled(stored.gfmAutoReportEnabled ?? null);
      setGfmNotificationCadence(stored.gfmNotificationCadence ?? null);
      setGfmDefaulterThreshold(stored.gfmDefaulterThreshold ?? null);
      setGfmWhatsappEnabled(stored.gfmWhatsappEnabled ?? null);
      setEffective(data.effective ?? null);

      toast.success("GFM configuration saved.");
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to save GFM configuration.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Guardian Faculty Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Institute-level defaults for the Guardian Faculty Member feature.
            Leave any field on &quot;Use system default&quot; to inherit the
            platform default.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfig}
            disabled={loading || saving}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSave}
            disabled={loading || saving}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 3 }, mt: 1 }}>
        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setLoadError(null)}
          >
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* GFM Enabled */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="gfm-enabled-label">
                  Guardian Faculty Enabled
                </InputLabel>
                <Select
                  labelId="gfm-enabled-label"
                  label="Guardian Faculty Enabled"
                  value={boolToSelect(gfmEnabled)}
                  onChange={(e) =>
                    setGfmEnabled(selectToBool(e.target.value))
                  }
                >
                  <MenuItem value={DEFAULT_SENTINEL}>
                    <em>Use system default</em>
                  </MenuItem>
                  <MenuItem value="true">Enabled</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Master switch for the GFM feature across this institute.
              </Typography>
            </Grid>

            {/* Auto Report Enabled */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="gfm-auto-report-label">
                  Automatic Reports
                </InputLabel>
                <Select
                  labelId="gfm-auto-report-label"
                  label="Automatic Reports"
                  value={boolToSelect(gfmAutoReportEnabled)}
                  onChange={(e) =>
                    setGfmAutoReportEnabled(selectToBool(e.target.value))
                  }
                >
                  <MenuItem value={DEFAULT_SENTINEL}>
                    <em>Use system default</em>
                  </MenuItem>
                  <MenuItem value="true">Enabled</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Generate and send scheduled defaulter reports automatically.
              </Typography>
            </Grid>

            {/* Send Reports over WhatsApp */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="gfm-whatsapp-label">
                  Send Reports over WhatsApp
                </InputLabel>
                <Select
                  labelId="gfm-whatsapp-label"
                  label="Send Reports over WhatsApp"
                  value={boolToSelect(gfmWhatsappEnabled)}
                  onChange={(e) =>
                    setGfmWhatsappEnabled(selectToBool(e.target.value))
                  }
                >
                  <MenuItem value={DEFAULT_SENTINEL}>
                    <em>Use system default</em>
                  </MenuItem>
                  <MenuItem value="true">Enabled</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                When disabled, defaulter reports are sent by email only —
                WhatsApp delivery is skipped and hidden from the defaulter page.
              </Typography>
            </Grid>

            {/* Notification Cadence */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="gfm-cadence-label">
                  Notification Cadence
                </InputLabel>
                <Select
                  labelId="gfm-cadence-label"
                  label="Notification Cadence"
                  value={gfmNotificationCadence ?? DEFAULT_SENTINEL}
                  onChange={(e) =>
                    setGfmNotificationCadence(
                      e.target.value === DEFAULT_SENTINEL
                        ? null
                        : e.target.value
                    )
                  }
                >
                  <MenuItem value={DEFAULT_SENTINEL}>
                    <em>Use system default</em>
                  </MenuItem>
                  {CADENCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                How often the reporting window recurs by default.
              </Typography>
            </Grid>

            {/* Defaulter Threshold */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={thresholdUsesDefault}
                    onChange={handleThresholdDefaultToggle}
                    color="primary"
                  />
                }
                label="Use system default threshold"
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Defaulter Threshold (%)"
                value={thresholdUsesDefault ? "" : gfmDefaulterThreshold}
                onChange={handleThresholdChange}
                disabled={thresholdUsesDefault}
                error={!!thresholdError}
                helperText={
                  thresholdError ||
                  "Students below this attendance % are flagged as defaulters."
                }
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{ mt: 1 }}
              />
            </Grid>

            {/* Effective values summary */}
            {effective && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Effective Settings (after applying defaults)
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={`Enabled: ${effective.gfmEnabled ? "Yes" : "No"}`}
                    color={effective.gfmEnabled ? "success" : "default"}
                  />
                  <Chip
                    size="small"
                    label={`Auto reports: ${
                      effective.gfmAutoReportEnabled ? "Yes" : "No"
                    }`}
                    color={
                      effective.gfmAutoReportEnabled ? "success" : "default"
                    }
                  />
                  <Chip
                    size="small"
                    label={`Cadence: ${cadenceLabel(effective.cadence)}`}
                  />
                  <Chip
                    size="small"
                    label={`Threshold: ${effective.threshold ?? "—"}%`}
                  />
                  <Chip
                    size="small"
                    label={`WhatsApp: ${
                      effective.gfmWhatsappEnabled ? "Yes" : "No"
                    }`}
                    color={
                      effective.gfmWhatsappEnabled ? "success" : "default"
                    }
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default GfmSettings;
